import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useYear } from "@/context/YearContext";
import { formatINR, formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, ChevronUp, ChevronDown, Pencil, Check, X, ShieldCheck, UserCog, CalendarClock, ScrollText } from "lucide-react";

export default function Settings() {
  const { year } = useYear();
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [rolloverReason, setRolloverReason] = useState("");
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = useCallback(async () => {
    const [c, u, a] = await Promise.all([
      api.get("/categories", { params: { include_archived: true } }),
      api.get("/users"),
      api.get("/audit", { params: { limit: 100 } }),
    ]);
    setCategories(c.data);
    setUsers(u.data);
    setAuditLog(a.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addCategory = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/categories", { name: newName });
      toast.success("Category added");
      setNewName("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/categories/${id}`, { name: editName });
      setEditId(null);
      toast.success("Category renamed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    }
  };

  const toggleActive = async (cat) => {
    await api.put(`/categories/${cat.id}`, { active: !cat.active });
    toast.success(cat.active ? `"${cat.name}" archived` : `"${cat.name}" activated`);
    load();
  };

  const move = async (idx, dir) => {
    const arr = [...categories];
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    setCategories(arr);
    await api.post("/categories/reorder", { ordered_ids: arr.map((c) => c.id) });
  };

  const rollover = async () => {
    if (!rolloverReason.trim()) { toast.error("A written reason/confirmation note is required"); return; }
    if (!window.confirm(`Close financial year ${year} and roll over?\n\n• Next-Year Reserve → ${year + 1} Operating Reserve\n• Unspent I&G, Founder Savings and unpaid taxes carry forward\n• ${year} budget will be locked. Historical figures remain unchanged.`)) return;
    try {
      const { data } = await api.post("/rollover", { year, reason: rolloverReason });
      toast.success(`Year ${year} rolled over to ${year + 1}`);
      setRolloverReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Rollover failed"); }
  };

  return (
    <div data-testid="settings-view-container" className="space-y-8">
      <div className="fade-up">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Admin controls · Category management & users</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="card-surface p-6 lg:col-span-3 fade-up" data-testid="category-manager">
          <div className="flex items-center justify-between mb-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Main Categories</p>
            <span className="text-[11px] text-zinc-400">{categories.filter((c) => c.active).length} active · {categories.filter((c) => !c.active).length} archived</span>
          </div>

          <div className="flex gap-2 mb-5">
            <Input data-testid="category-name-input" placeholder="New category, e.g. Marketing"
              className="bg-[#F8F7F3] border-[#E3E0D8]" value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCategory()} />
            <Button data-testid="category-add-btn" className="bg-blue-600 hover:bg-blue-500 text-white shrink-0" onClick={addCategory}>
              <Plus size={15} className="mr-1" /> Add
            </Button>
          </div>

          <div className="space-y-2">
            {categories.map((cat, idx) => (
              <div key={cat.id} data-testid={`category-row-${cat.id}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border border-[#ECE9E1] bg-[#FCFBF8] transition-opacity ${!cat.active ? "opacity-50" : ""}`}>
                <div className="flex flex-col gap-0.5">
                  <button data-testid={`category-up-${cat.id}`} onClick={() => move(idx, -1)} disabled={idx === 0}
                    className="text-zinc-400 hover:text-blue-600 disabled:opacity-30"><ChevronUp size={14} /></button>
                  <button data-testid={`category-down-${cat.id}`} onClick={() => move(idx, 1)} disabled={idx === categories.length - 1}
                    className="text-zinc-400 hover:text-blue-600 disabled:opacity-30"><ChevronDown size={14} /></button>
                </div>
                {editId === cat.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input data-testid="category-edit-input" className="h-8 bg-[#F8F7F3] border-[#E3E0D8]" value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveEdit(cat.id)} autoFocus />
                    <button data-testid="category-save-btn" onClick={() => saveEdit(cat.id)} className="text-emerald-600 p-1"><Check size={16} /></button>
                    <button onClick={() => setEditId(null)} className="text-zinc-500 p-1"><X size={16} /></button>
                  </div>
                ) : (
                  <>
                    <span className="flex-1 font-medium text-zinc-800 text-sm">{cat.name}</span>
                    {!cat.active && <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-[#E3E0D8] rounded-full px-2 py-0.5">Archived</span>}
                    <button data-testid={`category-edit-${cat.id}`} onClick={() => { setEditId(cat.id); setEditName(cat.name); }}
                      className="text-zinc-500 hover:text-blue-600 p-1"><Pencil size={13} /></button>
                    <Switch data-testid={`category-toggle-${cat.id}`} checked={cat.active} onCheckedChange={() => toggleActive(cat)} />
                  </>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-400 mt-4">Archiving hides a category from new expenses without deleting historical budget data.</p>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="card-surface p-6 fade-up" data-testid="users-panel">
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-5">Users</p>
            <div className="space-y-3">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-3 px-4 py-3.5 rounded-lg border border-[#ECE9E1] bg-[#FCFBF8]">
                  <div className="w-9 h-9 rounded-full bg-[#F8F7F3] border border-[#E3E0D8] flex items-center justify-center">
                    {u.role === "admin" ? <ShieldCheck size={15} className="text-blue-600" /> : <UserCog size={15} className="text-zinc-500" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-zinc-800">{u.name}</p>
                    <p className="text-xs text-zinc-500">{u.email}</p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider rounded-full px-2.5 py-1 border ${
                    u.role === "admin" ? "text-blue-600 border-blue-600/30 bg-blue-600/10" : "text-zinc-500 border-[#E3E0D8]"
                  }`}>{u.role}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-400 mt-4 leading-relaxed">
              Admin has full control including Settings. Accountant manages budgets, expenses and closings but cannot modify system rules or users.
              Google authentication will be enabled later — the system is already prepared for it.
            </p>
          </div>

          <div className="card-surface p-6 fade-up" data-testid="rollover-panel">
            <div className="flex items-center gap-2 mb-4">
              <CalendarClock size={14} className="text-blue-600" />
              <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Close Financial Year & Roll Over</p>
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed mb-3">
              Admin-only. Moves the {year} Next-Year Reserve into the {year + 1} Operating Reserve, carries forward unspent I&G, Founder Savings and unpaid taxes, and locks the {year} budget. Historical figures stay unchanged and a full audit entry is created.
            </p>
            <Textarea data-testid="rollover-reason-input" rows={2} className="bg-[#F8F7F3] border-[#E3E0D8] mb-3"
              placeholder="Mandatory reason / confirmation note" value={rolloverReason} onChange={(e) => setRolloverReason(e.target.value)} />
            <Button data-testid="rollover-btn" className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" onClick={rollover}>
              Close FY {year} & Roll Over to {year + 1}
            </Button>
          </div>

          <div className="card-surface p-6 fade-up" data-testid="rules-panel">
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4">System Rules (Fixed)</p>
            <ul className="space-y-2.5 text-xs text-zinc-500 leading-relaxed">
              <li>· Next-year reserve target = higher of current-year Core budget × <span className="text-blue-600 font-semibold">120%</span> or the approved next-year Core budget</li>
              <li>· Waterfall order: Reserve → 30% Founder Savings → 70% Innovation & Growth</li>
              <li>· Reserve shortfalls carry forward; Founder & Growth stay ₹0 until covered</li>
              <li>· Growth expenses only from Innovation & Growth Fund</li>
              <li>· Unspent I&G balance carries forward automatically</li>
              <li>· Actual recurring expenses reduce the Operating Reserve</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="card-surface fade-up overflow-hidden" data-testid="audit-log-panel">
        <div className="px-6 pt-6 pb-3 flex items-center gap-2">
          <ScrollText size={14} className="text-blue-600" />
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Audit Log</p>
          <span className="text-[11px] text-zinc-400">last {auditLog.length} events</span>
        </div>
        <div className="overflow-x-auto max-h-[480px]">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-[#ECE9E1]">
                <th className="px-6 py-2.5 font-medium">When</th>
                <th className="px-3 py-2.5 font-medium">User</th>
                <th className="px-3 py-2.5 font-medium">Action</th>
                <th className="px-3 py-2.5 font-medium">Entity</th>
                <th className="px-3 py-2.5 font-medium">Year / Month</th>
                <th className="px-3 py-2.5 font-medium">Change</th>
                <th className="px-6 py-2.5 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {auditLog.length === 0 && <tr><td colSpan={7} className="px-6 py-8 text-center text-zinc-500">No audit events yet</td></tr>}
              {auditLog.map((a) => (
                <tr key={a.id} className="border-b border-[#ECE9E1] last:border-0">
                  <td className="px-6 py-2.5 text-xs text-zinc-500 whitespace-nowrap">{formatDateTime(a.timestamp)}</td>
                  <td className="px-3 py-2.5 text-xs text-zinc-700">{a.user}</td>
                  <td className="px-3 py-2.5">
                    <span className="text-[10px] uppercase tracking-wide font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">{a.action.replace(/_/g, " ")}</span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-zinc-500">{a.entity}</td>
                  <td className="px-3 py-2.5 text-xs text-zinc-500 font-mono-num">{a.year || "—"}{a.month ? ` / ${a.month}` : ""}</td>
                  <td className="px-3 py-2.5 text-[11px] text-zinc-500 max-w-[260px]">
                    {a.original && <span className="line-through text-zinc-400 mr-1">{JSON.stringify(a.original).slice(0, 60)}</span>}
                    {a.new && <span>{JSON.stringify(a.new).slice(0, 80)}</span>}
                  </td>
                  <td className="px-6 py-2.5 text-xs text-zinc-500 max-w-[180px] truncate" title={a.reason}>{a.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
